/**
 * People Formatting Utilities
 * Centralized initials/name helpers used across avatars, filters, and tables.
 */

/**
 * Get avatar-style initials from a free-text display name (first word + last word).
 * Matches the exact semantics previously duplicated verbatim across
 * TeamActivityTable, OutreachRepPerformanceTable, LinkedInRepPerformanceTable,
 * and EmailRepPerformanceTable.
 * @param name - Free-text name, e.g. "John Michael Smith" -> "JS"
 * @returns Up to 2 uppercase initials, or '?' if name is empty
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
}

/**
 * Initials from the first two words of a free-text name ("Mary Jane Watson" →
 * "MJ"), or '?' when there are none. Unlike {@link getInitials} this never skips
 * to the last word, which suits speaker labels and attendee names where the
 * second word is the surname or a role ("Dana (Host)"). Replaces the copies that
 * PersonAvatar, PanelInitials, MeetingsWorkspace, MeetingTranscript and
 * MeetingPrepSections each carried.
 */
export function getLeadingInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('') || '?';
}

/**
 * Title-case a free-text display name for presentation (e.g. rep performance
 * tables), leaving email-address fallbacks untouched. The backend's
 * display_name is "first_name last_name" stored as typed — lowercase for most
 * users — falling back to a raw email when no name is set. Title-casing an
 * email would read as broken ("Onboarding@Marketbetter.Ai"), so names and
 * emails need different treatment rather than one blind capitalize-every-word.
 * @param name - e.g. "kyle hanna" -> "Kyle Hanna"; "jose@marketbetter.ai" -> unchanged
 */
export function formatDisplayName(name: string): string {
  if (!name || name.includes('@')) return name;
  return name
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Minimal shape needed to derive a workspace user's initials. */
export interface InitialsUser {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/**
 * Get avatar-style initials from a workspace user record, preferring
 * first+last name initials, falling back to name/email prefix, then 'U'.
 * Matches the exact semantics previously duplicated verbatim across
 * the owner picker, MeetingScopeFilter, and SdrFilter.
 */
export function getUserInitials(user: InitialsUser): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user.first_name) {
    return user.first_name.substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return 'U';
}

/**
 * Label a workspace user in a filter/picker list: full name when known, first
 * name alone when that is all we have, email as the last resort (never a blank
 * option). Matches the semantics previously duplicated verbatim across
 * SdrFilter, MeetingScopeFilter, and the audiences owner filter.
 */
export function getUserDisplayName(user: InitialsUser): string {
  const first = user.first_name?.trim() || '';
  const last = user.last_name?.trim() || '';
  // Sign-up fills first_name with the email's local part when no name was given,
  // so "hieuvo150405" and "anusha" arrived as names and were listed verbatim.
  const firstIsPlaceholder = !!first && !last && isEmailLocalPart(first, user.email);
  if (first && !firstIsPlaceholder) return formatDisplayName(last ? `${first} ${last}` : first);
  if (firstIsPlaceholder) return (user.email && nameFromEmail(user.email)) || first;
  // No name at all: the email itself, which is unambiguous where no second line shows it.
  return user.email || 'Unknown user';
}

/** Whether a stored name is just the email's local part rather than a real name. */
function isEmailLocalPart(name: string, email: string | null | undefined): boolean {
  if (!email || !email.includes('@')) return false;
  return name.toLowerCase() === email.split('@')[0].toLowerCase();
}

/**
 * A readable name from an email's local part: separators become spaces, digit
 * runs are dropped and each word is capitalised ("hieuvo150405@…" -> "Hieuvo",
 * "mary.jones@…" -> "Mary Jones"). Null when nothing name-like remains, so the
 * caller falls back to the email itself.
 */
export function nameFromEmail(email: string): string | null {
  const local = email.split('@')[0] || '';
  const words = local
    .split(/[._+-]+/)
    .map((word) => word.replace(/\d+/g, ''))
    .filter((word) => /[a-z]/i.test(word));
  if (words.length === 0) return null;
  return words.map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}
